// src/frontend/src/router/index.js
import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/store/auth';

// Views
const LoginView = () => import('@/views/LoginView.vue');
const ArticlesView = () => import('@/views/ArticlesView.vue');
const UploadView = () => import('@/views/UploadView.vue');
const AdminUsersView = () => import('@/views/admin/UsersView.vue');
const AdminImportExportView = () => import('@/views/admin/ImportExportView.vue');
const AdminFilesView = () => import('@/views/admin/FilesView.vue');

// Split model (ticket 0055): two sub-sites over a shared article layer.
const OsiListView = () => import('@/views/osi/OsiListView.vue');
const OsiDetailView = () => import('@/views/osi/OsiDetailView.vue');
const OsiNewView = () => import('@/views/osi/OsiNewView.vue');
const SnapshotListView = () => import('@/views/snapshot/SnapshotListView.vue');
const SnapshotDetailView = () => import('@/views/snapshot/SnapshotDetailView.vue');
const SnapshotNewView = () => import('@/views/snapshot/SnapshotNewView.vue');
const ArticleDetailView = () => import('@/views/ArticleDetailView.vue');

const routes = [
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    meta: { requiresAuth: false }
  },
  {
    path: '/',
    name: 'articles',
    component: ArticlesView,
    meta: { requiresAuth: true }
  },
  {
    path: '/articles',
    redirect: '/'
  },
  {
    path: '/my-work',
    name: 'my-work',
    component: () => import('@/views/MyWorkView.vue'),
    meta: { requiresAuth: true }
  },
  // The pre-split document pages are gone; an article is now the per-DOI view.
  {
    path: '/documents',
    redirect: '/'
  },
  {
    path: '/documents/:id',
    redirect: '/'
  },
  {
    path: '/upload',
    name: 'upload',
    component: UploadView,
    meta: { requiresAuth: true }
  },
  // ── OSI sub-site ─────────────────────────────────────────────────────────
  // No policy anywhere in this tree.
  {
    path: '/osi',
    name: 'osi-list',
    component: OsiListView,
    meta: { requiresAuth: true, side: 'osi' }
  },
  {
    path: '/osi/new',
    name: 'osi-new',
    component: OsiNewView,
    meta: { requiresAuth: true, side: 'osi' }
  },
  {
    path: '/osi/:id',
    name: 'osi-detail',
    component: OsiDetailView,
    meta: { requiresAuth: true, side: 'osi' }
  },

  // ── Snapshot sub-site ────────────────────────────────────────────────────
  // Keeps the editorial-policy dimension.
  {
    path: '/snapshot',
    name: 'snapshot-list',
    component: SnapshotListView,
    meta: { requiresAuth: true, side: 'snapshot' }
  },
  {
    path: '/snapshot/new',
    name: 'snapshot-new',
    component: SnapshotNewView,
    meta: { requiresAuth: true, side: 'snapshot' }
  },
  {
    path: '/snapshot/:id',
    name: 'snapshot-detail',
    component: SnapshotDetailView,
    meta: { requiresAuth: true, side: 'snapshot' }
  },

  // ── Shared layer ─────────────────────────────────────────────────────────
  {
    path: '/articles/:id',
    name: 'article-detail',
    component: ArticleDetailView,
    meta: { requiresAuth: true }
  },

  {
    path: '/admin/files',
    name: 'admin-files',
    component: AdminFilesView,
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/users',
    name: 'admin-users',
    component: AdminUsersView,
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/import-export',
    name: 'admin-import-export',
    component: AdminImportExportView,
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/'
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes
});

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();

  // Initialize auth state if needed
  if (!authStore.initialized) {
    await authStore.initialize();
  }

  const requiresAuth = to.meta.requiresAuth !== false;
  const requiresAdmin = to.meta.requiresAdmin === true;

  if (requiresAuth && !authStore.isAuthenticated) {
    next({ name: 'login', query: { redirect: to.fullPath } });
    return;
  }

  if (requiresAdmin && !authStore.isAdmin) {
    next({ name: 'articles' });
    return;
  }

  if (to.name === 'login' && authStore.isAuthenticated) {
    next({ name: 'articles' });
    return;
  }

  next();
});

export default router;
